/**
 * Barra de envío gratis (sections/mx-shipping-bar.liquid).
 *
 * Shopify entrega el primer mensaje ya calculado. Este archivo solo:
 * - vuelve a calcular cuando cambia el carro (carro lateral, botón "Agregar al carro" o el aviso de Loft);
 * - permite cerrarla durante la visita.
 */
const DISMISS_KEY = 'mx-shipping-bar-dismissed';

class MxShippingBar extends HTMLElement {
  #pending = null;

  connectedCallback() {
    if (this.dataset.dismissible === 'true' && this.#wasDismissed()) {
      this.classList.add('is-hidden');
      return;
    }

    this.querySelector('[data-mx-sb-close]')?.addEventListener('click', this.#dismiss);
    document.addEventListener('mx:cart:changed', this.#update);
    document.body.addEventListener('theme:ajaxCart:addSuccess', this.#update);
    // Al volver con el botón Atrás, la página puede venir de la caché con un total antiguo.
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    document.removeEventListener('mx:cart:changed', this.#update);
    document.body.removeEventListener('theme:ajaxCart:addSuccess', this.#update);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) this.#update();
  };

  #update = async () => {
    this.#pending?.abort();
    this.#pending = new AbortController();

    try {
      const response = await fetch(`${this.dataset.cartUrl}.js`, { signal: this.#pending.signal });
      if (!response.ok) return;
      const cart = await response.json();
      this.#render(cart.total_price, cart.item_count);
    } catch {}
  };

  /**
   * @param {number} totalCents
   * @param {number} itemCount
   */
  #render(totalCents, itemCount) {
    const threshold = Number(this.dataset.threshold);
    const remaining = threshold - totalCents;
    const state = itemCount === 0 ? 'empty' : remaining > 0 ? 'remaining' : 'reached';
    const progress = Math.max(0, Math.min(100, Math.floor((totalCents * 100) / threshold)));

    const template = {
      empty: this.dataset.textEmpty,
      remaining: this.dataset.textRemaining,
      reached: this.dataset.textReached,
    }[state];

    const text = this.querySelector('[data-mx-sb-text]');
    if (text) {
      // Los textos vienen escapados desde Liquid (data-*); el navegador ya los decodificó, así que se
      // insertan como texto y solo los montos van en negrita.
      text.replaceChildren(...this.#fill(template || '', { umbral: threshold, monto: state === 'empty' ? threshold : remaining }));
    }

    this.querySelector('[data-mx-sb-track]')?.setAttribute('aria-valuenow', String(progress));
    const fill = /** @type {HTMLElement | null} */ (this.querySelector('[data-mx-sb-fill]'));
    if (fill) fill.style.width = `${progress}%`;

    this.classList.remove('mx-sb--empty', 'mx-sb--remaining', 'mx-sb--reached');
    this.classList.add(`mx-sb--${state}`);
  }

  /**
   * Reemplaza [umbral] y [monto] por montos en negrita.
   * @param {string} template
   * @param {Record<string, number>} amounts - en centavos
   */
  #fill(template, amounts) {
    return template.split(/(\[umbral\]|\[monto\])/).map((part) => {
      const key = part.slice(1, -1);
      if (part.startsWith('[') && key in amounts) {
        const strong = document.createElement('strong');
        strong.textContent = formatMoney(amounts[key]);
        return strong;
      }
      return document.createTextNode(part);
    });
  }

  #dismiss = () => {
    this.classList.add('is-hidden');
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {}
  };

  #wasDismissed() {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  }
}

/**
 * Pesos chilenos sin decimales, como el resto de la tienda: $35.000
 * @param {number} cents
 */
function formatMoney(cents) {
  return `$${Math.round(cents / 100).toLocaleString('es-CL')}`;
}

if (!customElements.get('mx-shipping-bar')) customElements.define('mx-shipping-bar', MxShippingBar);
